import { cn } from "@/lib/utils/cn";
import { generateSkeletonArray } from "@/lib/utils/skeletonUtils";
import { SkeletonBase } from "./SkeletonBase";

/**
 * Table skeleton loader
 * @param {Object} props - Component props
 * @param {number} props.rows - Number of table rows to display (default: 5)
 * @param {number} props.columns - Number of table columns to display (default: 4)
 * @param {boolean} props.hasHeader - Whether to show table header (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {Array<string|number>} props.columnWidths - Optional per-column widths; when provided, skeleton blocks use these instead of flex-1
 */
export function TableSkeleton({
  rows = 5,
  columns = 4,
  hasHeader = true,
  className,
  columnWidths = [],
}) {
  const rowArray = generateSkeletonArray(rows);
  const columnArray = generateSkeletonArray(columns);

  return (
    <div className={cn("w-full space-y-3", className)}>
      {/* Table Header */}
      {hasHeader && (
        <div className="flex items-center gap-4 pb-3 border-b border-base-300">
          {columnArray.map((colIndex) => {
            const w = columnWidths[colIndex];
            return (
              <div
                key={`header-${colIndex}`}
                className={cn(!w && "flex-1")}
                style={w ? { width: w, flexShrink: 0 } : undefined}
              >
                <SkeletonBase variant="line" className="h-5" />
              </div>
            );
          })}
        </div>
      )}

      {/* Table Rows */}
      <div className="space-y-3">
        {rowArray.map((rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex items-center gap-4">
            {columnArray.map((colIndex) => {
              const w = columnWidths[colIndex];
              return (
                <div
                  key={`cell-${rowIndex}-${colIndex}`}
                  className={cn(!w && "flex-1")}
                  style={w ? { width: w, flexShrink: 0 } : undefined}
                >
                  <SkeletonBase variant="line" />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
